/**
 * Returns the most recent N audit events for the current session, in
 * reverse-chronological order. Drives the "last 5 actions" panel in the
 * bug-report form (P5/P7 — every effect has a receipt; this surfaces them).
 *
 * Convex Session D row-split (2026-04-29) made `auditEvents` a per-row
 * table indexed by (sessionId, ts). Reads merge back into the legacy
 * `audit.events` map shape via audit:get, which the storage driver
 * normalizes into `audit.events`. Consumers (like this hook) can pull
 * directly from that normalized object — the audit timeline UI does the
 * same thing.
 *
 * Why a hook instead of a one-shot fetch in the submit handler:
 *  - The form previews the last-N panel as the user is filling it out, so
 *    it should refresh as new actions occur during composition (e.g. user
 *    pastes a screenshot, then fixes typo, then realizes another action
 *    just bubbled into audit).
 *  - The slider (1-20) lives in the form; the hook recomputes the slice
 *    when the user changes N.
 *
 * Reporter scoping: in a multi-user session every action lands in the
 * same audit table, so the form must filter to the *reporter's* actions
 * only — otherwise player A filing a bug while player B is mid-turn
 * shows mostly B's actions and the agent reasons about the wrong context.
 *
 * @param {object} audit  — the audit object from useStorage()
 * @param {number} n      — desired count (default 5, slider range 1-20)
 * @param {string|null} sessionId — limit to a session, default = active
 * @param {{ isDM?: boolean, charId?: string }|null} reporter — when set,
 *   restrict events to those whose actor matches the reporter (isDM wins
 *   over charId, mirroring App.js getActor()). When null, no actor filter.
 */

import React from 'react';

// Audit events all carry an `actor` of shape:
//   - DM:     { type: 'dm', name: 'DM' }
//   - Player: { type: 'player', name, charId }
// (See App.js getActor at line ~54430.)
export const matchesReporter = (event, reporter) => {
  if (!reporter) return true;
  const a = event && event.actor;
  if (!a) return false;
  if (reporter.isDM) return a.type === 'dm';
  if (reporter.charId) return a.type === 'player' && a.charId === reporter.charId;
  // Reporter has no charId and isn't DM — auth in flux. Don't leak other
  // users' events; show none rather than wrong ones.
  return false;
};

export function useLastAuditEvents(audit, n = 5, sessionId = undefined, reporter = null) {
  const reporterIsDM = reporter ? !!reporter.isDM : null;
  const reporterCharId = reporter ? (reporter.charId || null) : null;
  return React.useMemo(() => {
    if (!audit || !audit.events) return [];
    const events = Object.values(audit.events).filter(Boolean);
    const targetSessionId = sessionId === undefined ? audit.activeSessionId : sessionId;
    const sessionFiltered = targetSessionId
      ? events.filter(e => e.sessionId === targetSessionId)
      : events;
    const reporterFiltered = (reporterIsDM === null && reporterCharId === null)
      ? sessionFiltered
      : sessionFiltered.filter(e => matchesReporter(e, { isDM: reporterIsDM, charId: reporterCharId }));
    return reporterFiltered
      .sort((a, b) => (b.ts || 0) - (a.ts || 0))
      .slice(0, Math.max(0, Math.min(n, 50)))
      .map(e => ({
        ts: e.ts,
        category: e.category,
        action: e.action,
        actor: e.actor,
        target: e.target,
        summary: e.summary,
        importance: e.importance,
      }));
  }, [audit, n, sessionId, reporterIsDM, reporterCharId]);
}
